import { createDocumentEntityId } from '../ids/entityIds.js';
import { openTemplateLabel } from '../editor/historyLabels.js';
import { deserializeFile } from './file.js';
import { collectDocumentFontIds } from '../state/documentFonts.js';
import { ensureBundledFontsLoaded } from '../sdf/shapeEngine.js';
import type { AppState } from '../state/appState.js';
import { resolveDataField } from '../graph/dataSourceSchema.js';
import type { DataFieldType, DataSourceDef } from '../graph/dataGraph.js';
import { readKvCollection, type KvCollections } from './dataProviders.js';

interface TemplateLoaderDeps {
  appState: AppState;
  resetPan: () => void;
  resetHistory: (label?: string) => void;
  renderGraph: () => void;
  updateParams: () => void;
  renderSceneTree: () => void;
  renderEditorNavigation: () => void;
  syncToolbar?: () => void;
  revealToolbar?: () => void;
  scheduleRender: () => void;
  dataSourceBaseUrl?: string;
  dataSourceUrls?: Record<string, string>;
  /** The project's data its `kv` sources read. */
  projectData?: KvCollections;
  setDataSources?: (sources: Record<string, unknown>) => void;
  setLoadedDataSources?: (records: Record<string, unknown>, documents: Record<string, unknown>) => void;
}

export interface LoadedDataSources {
  records: Record<string, unknown>;
  documents: Record<string, unknown>;
}

/**
 * Read a document's data sources before its first frame: a `kv` source from
 * the project's data, a `json` or `rest` one fetched against `baseUrl`. A host
 * that hands neither reads nothing of that kind.
 */
export async function loadDataSources(
  appState: AppState,
  baseUrl: string | undefined,
  urls: Record<string, string> = {},
  projectData?: KvCollections,
): Promise<LoadedDataSources> {
  const records: Record<string, unknown> = {};
  const documents: Record<string, unknown> = {};
  for (const [id, source] of Object.entries(appState.dataSources)) {
    if (source.provider === 'kv') {
      if (!projectData) continue;
      const document = readKvCollection(projectData, source.resource);
      records[id] = normalizeDataSourceDocument(document, source, id);
      documents[id] = document;
      continue;
    }
    if (!baseUrl) continue;
    if (source.provider !== 'json' && source.provider !== 'rest') {
      throw new Error(`Data source '${id}' provider '${source.provider}' requires a registered host provider`);
    }
    const response = await fetch(urls[source.resource] ?? new URL(source.resource, baseUrl));
    if (!response.ok) throw new Error(`Data source '${id}' failed to load: ${response.status}`);
    const document: unknown = await response.json();
    const rows = normalizeDataSourceDocument(document, source, id);
    records[id] = rows;
    documents[id] = document;
  }
  return { records, documents };
}

export function normalizeDataSourceDocument(document: unknown, source: DataSourceDef, id: string): unknown {
    // A `kv` collection is the records by key: the path walks a JSON resource.
    const rows = source.provider === 'kv' ? document : resolveDataRecords(document, source.records.path, id);
    if (source.records.kind === 'array' && !Array.isArray(rows)) throw new Error(`Data source '${id}' records path must resolve to an array`);
    if (source.records.kind === 'map' && (!rows || typeof rows !== 'object' || Array.isArray(rows))) throw new Error(`Data source '${id}' records path must resolve to an object`);
    if (source.records.kind === 'array' && source.keyType !== 'number') throw new Error(`Data source '${id}' array records require a number key`);
    for (const [key, row] of Object.entries(rows as Record<string, unknown>)) {
      if (!matchesKeyType(key, source.keyType)) {
        throw new Error(`Data source '${id}' key '${key}' is not a ${source.keyType}`);
      }
      if (!row || typeof row !== 'object' || Array.isArray(row)) throw new Error(`Data source '${id}' row '${key}' must be an object`);
      for (const [fieldId, field] of Object.entries(source.fields)) {
        const value = resolveDataField(row, field.path);
        if (value === undefined) throw new Error(`Data source '${id}' row '${key}' is missing '${field.path.join(' › ')}'`);
        if (!matchesValueType(value, field.type)) {
          throw new Error(`Data source '${id}' row '${key}' field '${fieldId}' is not a ${field.type}`);
        }
      }
    }
    return projectDataRecords(rows, source);
}

export function resolveDataRecords(document: unknown, path: Array<string | number>, sourceId: string): unknown {
  let value = document;
  for (const [index, segment] of path.entries()) {
    if (typeof segment === 'number') {
      if (!Array.isArray(value) || !Number.isInteger(segment) || segment < 0 || segment >= value.length) {
        throw new Error(`Data source '${sourceId}' records path[${index}] does not address an array item`);
      }
      value = value[segment];
    } else {
      if (!value || typeof value !== 'object' || Array.isArray(value) || !(segment in value)) {
        throw new Error(`Data source '${sourceId}' records path[${index}] key '${segment}' was not found`);
      }
      value = (value as Record<string, unknown>)[segment];
    }
  }
  return value;
}

export function projectDataRecords(records: unknown, source: DataSourceDef): unknown {
  const project = (record: unknown): Record<string, unknown> => ({
    ...(record as Record<string, unknown>),
    ...Object.fromEntries(Object.entries(source.fields).map(([fieldId, field]) => [fieldId, resolveDataField(record, field.path)])),
  });
  if (Array.isArray(records)) return records.map(project);
  return Object.fromEntries(Object.entries(records as Record<string, unknown>).map(([key, record]) => [key, project(record)]));
}

function matchesKeyType(key: string, type: AppState['dataSources'][string]['keyType']): boolean {
  if (type === 'string') return true;
  if (type === 'bool') return key === 'true' || key === 'false';
  const value = Number(key);
  return Number.isFinite(value) && String(value) === key;
}

function matchesValueType(value: unknown, type: DataFieldType): boolean {
  if (value === null) return true;
  if (type === 'bool') return typeof value === 'boolean';
  if (type === 'number') return typeof value === 'number' && Number.isFinite(value);
  if (type === 'string') return typeof value === 'string';
  const components = Number(type.slice(3));
  return Array.isArray(value)
    && value.length === components
    && value.every(component => typeof component === 'number' && Number.isFinite(component));
}

export function applyTemplateToState(appState: AppState, template: string | Uint8Array): void {
  deserializeFile(appState, template);
  appState.documentId = createDocumentEntityId();
}

export function deserializeTemplate(appState: AppState, data: string | Uint8Array): void {
  applyTemplateToState(appState, data);
}

export async function loadTemplateWithDeps(
  data: string | Uint8Array,
  deps: TemplateLoaderDeps,
  name: string,
): Promise<void> {
  deserializeTemplate(deps.appState, data);
  const sources = await loadDataSources(deps.appState, deps.dataSourceBaseUrl, deps.dataSourceUrls, deps.projectData);
  if (deps.setLoadedDataSources) deps.setLoadedDataSources(sources.records, sources.documents);
  else deps.setDataSources?.(sources.records);
  await ensureBundledFontsLoaded(collectDocumentFontIds(deps.appState));

  deps.resetPan();
  deps.resetHistory(openTemplateLabel(name));
  deps.renderGraph();
  deps.updateParams();
  deps.renderSceneTree();
  deps.renderEditorNavigation();
  deps.syncToolbar?.();
  deps.revealToolbar?.();
  deps.scheduleRender();
}
